import { Navigate, Route, Routes } from 'react-router-dom';
import type { Permission } from '@ias/shared';

import { AppShell } from '@/components/layout/app-shell';
import { EmptyState } from '@/components/common/page-header';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';

import { LoginPage } from '@/pages/login';
import { ForgotPasswordPage } from '@/pages/forgot-password';
import { ResetPasswordPage } from '@/pages/reset-password';
import { DashboardPage } from '@/pages/dashboard';
import { CollectionsPage } from '@/pages/collections';
import { NoticesPage } from '@/pages/notices';
import { ClientListPage } from '@/pages/clients/list';
import { ClientDetailPage } from '@/pages/clients/detail';
import { PolicyListPage } from '@/pages/policies/list';
import { PolicyDetailPage } from '@/pages/policies/detail';
import { RenewalsPage } from '@/pages/renewals';
import { ClaimListPage } from '@/pages/claims/list';
import { ClaimDetailPage } from '@/pages/claims/detail';
import { RemindersPage } from '@/pages/reminders';
import { CommissionsPage } from '@/pages/commissions';
import { ReportsPage } from '@/pages/reports';
import { InsurersPage } from '@/pages/insurers';
import { InsurerDetailPage } from '@/pages/insurer-detail';
import { UsersPage } from '@/pages/users';
import { ImportPage } from '@/pages/import';
import { AuditPage } from '@/pages/audit';
import { SettingsPage } from '@/pages/settings';

export function App() {
  const { user, initialising } = useAuth();

  // Blocks the first paint only while the refresh cookie is being checked, so a
  // signed-in reload never flashes the login screen.
  if (initialising) return <BootSplash />;

  // Signed out, every path is the login screen — except the two the password-reset email
  // leads to. They are the only routes that exist on both sides of the gate: the reset link
  // must work whether or not this browser happens to hold someone's session.
  if (!user) {
    return (
      <Routes>
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
        <Route path="reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route element={<AppShell />}>
        {/* D11 review outcome: the first view is the dashboard — general information from
            the whole system. «Η μέρα μου» retired; its high-value blocks live inside it. */}
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="notices" element={<Guarded permission="policy:read"><NoticesPage /></Guarded>} />
        {/* Kept reachable by URL though it is no longer in the navigation: instalments are
            still generated and still readable per policy, and pulling the route as well as
            the nav entry in one move would strand anyone holding a bookmark. */}
        <Route path="collections" element={<Guarded permission="policy:read"><CollectionsPage /></Guarded>} />

        <Route path="clients" element={<Guarded permission="client:read"><ClientListPage /></Guarded>} />
        <Route path="clients/:id" element={<Guarded permission="client:read"><ClientDetailPage /></Guarded>} />

        <Route path="policies" element={<Guarded permission="policy:read"><PolicyListPage /></Guarded>} />
        <Route path="policies/:id" element={<Guarded permission="policy:read"><PolicyDetailPage /></Guarded>} />
        <Route path="renewals" element={<Guarded permission="policy:read"><RenewalsPage /></Guarded>} />

        <Route path="claims" element={<Guarded permission="claim:read"><ClaimListPage /></Guarded>} />
        <Route path="claims/:id" element={<Guarded permission="claim:read"><ClaimDetailPage /></Guarded>} />

        <Route path="reminders" element={<Guarded permission="reminder:read"><RemindersPage /></Guarded>} />
        <Route path="commissions" element={<Guarded permission="commission:read"><CommissionsPage /></Guarded>} />
        <Route path="reports" element={<Guarded permission="report:read"><ReportsPage /></Guarded>} />
        <Route path="insurers" element={<Guarded permission="policy:read"><InsurersPage /></Guarded>} />
        <Route path="insurers/:id" element={<Guarded permission="policy:read"><InsurerDetailPage /></Guarded>} />
        <Route path="users" element={<Guarded permission="user:manage"><UsersPage /></Guarded>} />
        <Route path="import" element={<Guarded permission="import:run"><ImportPage /></Guarded>} />
        <Route path="audit" element={<Guarded permission="audit:read"><AuditPage /></Guarded>} />
        <Route path="settings" element={<SettingsPage />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/**
 * Route-level permission gate. The sidebar already hides links a role cannot use;
 * this catches the case of someone arriving by URL, and shows an explanation rather
 * than a blank screen.
 */
function Guarded({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { can } = useAuth();
  const { t } = useI18n();

  if (!can(permission)) {
    return (
      <EmptyState title={t('error.forbidden')} hint={t('error.forbiddenHint')} />
    );
  }
  return <>{children}</>;
}

function NotFound() {
  const { t } = useI18n();
  return <EmptyState title={t('error.notFound')} hint={t('error.notFoundHint')} />;
}

function BootSplash() {
  return (
    <div className="bg-background grid h-dvh place-items-center">
      <div className="flex flex-col items-center gap-3">
        <div className="border-primary size-7 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    </div>
  );
}

export { Navigate };
